import React, { useState, useEffect } from 'react'; // Added useEffect
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import {
    Button,
    TextField,
    Typography,
    Box,
    MenuItem,
    Select,
    FormControl,
    Paper
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite'; // Heart icon
import LanguageIcon from '@mui/icons-material/Language'; // Globe icon
import { useTranslation } from 'react-i18next';

const Login = () => {
    const { t, i18n } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const userRole = localStorage.getItem('userRole');
        if (userRole) {
            if (userRole === 'doctor' || userRole === 'admin' || userRole === 'occupational' || userRole === 'behavioral' || userRole === 'speech') {
                navigate('/doctor-dashboard', { replace: true });
            } else if (userRole === 'parent') {
                navigate('/parent-dashboard', { replace: true });
            }
        }
    }, [navigate]); // Dependency array includes navigate to prevent lint warnings

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${API_BASE_URL}/login.php`, { email, password });
            if (response.status === 200) {
                const { user_type, user_id, role } = response.data;
                const effectiveRole = user_type === 'doctor' ? (role || 'doctor') : user_type;
                localStorage.setItem('userRole', effectiveRole);
                localStorage.setItem('userId', user_id);
                if (user_type === 'doctor') {
                    navigate('/doctor-dashboard', { replace: true });
                } else if (user_type === 'parent') {
                    navigate('/parent-dashboard', { replace: true });
                }
            } else { // If response.status is not 200, it's an error
                setError(response.data.message || t('invalid_credentials'));
            }
        } catch (err) {
            // This catch block handles network errors or other exceptions
            console.error("Login error:", err);
            setError(t('invalid_credentials'));
        }
    };

    const changeLanguage = (event) => {
        i18n.changeLanguage(event.target.value);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'white',
                background: 'radial-gradient(circle at 0% 100%, rgba(224, 242, 241, 0.3) 0%, transparent 50%), radial-gradient(circle at 100% 0%, rgba(209, 196, 233, 0.3) 0%, transparent 50%)', // Faint purple-to-blue gradient
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                }}
            >
                {/* Left Header - Logo and Login Text */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            bgcolor: '#00bcd4', // Teal
                            borderRadius: '8px',
                            p: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <FavoriteIcon sx={{ color: 'white', fontSize: '20px' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {t('login')}
                    </Typography>
                </Box>

                {/* Right Header - Language Selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LanguageIcon sx={{ color: 'text.secondary' }} />
                    <FormControl variant="standard" sx={{ minWidth: 60 }}>
                        <Select
                            value={i18n.language}
                            onChange={changeLanguage}
                            disableUnderline
                            inputProps={{ 'aria-label': 'Without label' }}
                            sx={{ '.MuiSelect-select': { p: 0, pr: '24px !important' } }}
                        >
                            <MenuItem value="en">EN</MenuItem>
                            <MenuItem value="te">TE</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Central Card */}
            <Paper
                elevation={3}
                className="card-elevated fade-in"
                sx={{
                    p: 4,
                    borderRadius: '16px',
                    textAlign: 'center',
                    maxWidth: '400px',
                    width: '100%',
                    mx: 'auto',
                    my: 4,
                }}
            >
                {/* Card Top Logo and Title */}
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            bgcolor: '#9c27b0', // Purple
                            borderRadius: '8px',
                            p: 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <FavoriteIcon sx={{ color: 'white', fontSize: '30px' }} />
                    </Box>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mt: 1 }}>
                        AKIDSCARE
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                        {t('pediatric_development_specialist')}
                    </Typography>
                </Box>

                {/* Main Heading and Subheading */}
                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {t('welcome_to_akidscare')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('signin_to_access_dashboard')}
                </Typography>

                {/* Login Form */}
                <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label={t('email_placeholder')}
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="form-input"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label={t('password_placeholder')}
                        type="password"
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="form-input"
                        sx={{ mb: 2 }}
                    />
                    {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        className="btn-primary"
                        sx={{
                            mt: 3,
                            mb: 2,
                            py: 1.5,
                        }}
                    >
                        {t('login')}
                    </Button>
                </Box>

                {/* Sample Credentials Inset Box */}
                {/* <Box
                    sx={{
                        bgcolor: '#f5f5f5', // Light gray
                        p: 2,
                        borderRadius: '8px',
                        textAlign: 'left',
                        mt: 3,
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {t('sample_credentials')}
                    </Typography>
                    <Typography variant="body2">
                        Email: dr.pavan@example.com
                    </Typography>
                    <Typography variant="body2">
                        Password: password123
                    </Typography>
                </Box> */}
            </Paper>

            {/* Footer */}
            <Typography variant="caption" color="text.secondary" sx={{ p: 2 }}>
                {t('secure_platform_footer')}
            </Typography>
        </Box>
    );
};

export default Login;
